'use client'
import { useMemo, useState } from 'react'
import Link from 'next/link'
import Modal from '@crystal-eyes/components/elements/Modal'
import styles from './TaggingModal.module.scss'
import useTagging from '../hooks/useTagging'
import SectionCard from './SectionCard'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import useProfileTags from '@crystal-eyes/hooks/profiles/useProfileTags'
import useAllProfileTags from '@crystal-eyes/hooks/user/useAllProfileTags'
import Checkbox from '@crystal-eyes/components/elements/Input/Checkbox'

interface Identity {
  id: string
}

type Props = { identity?: Identity }

const DEFAULT_TAGS = [
  { name: 'other' },
  { name: 'customer' },
  { name: 'prospect' },
]

export default function EditProfileModal({ identity }: Props) {
  const [newTagName, setNewTagName] = useState<string>('')

  const { data: personality } = usePersonality({ identity })

  const {
    taggingState: { state: taggingState, setState: setTaggingState },
  } = useTagging({ identity })

  const { data: allTags = [], createTag, deleteTag } = useAllProfileTags()
  const {
    data: profileTags = [],
    addTag,
    removeTag,
  } = useProfileTags({ identity })

  const tagsWithDefaults: { id?: string; name: string; default?: boolean }[] =
    useMemo(() => {
      const defaultTagNames = DEFAULT_TAGS.map(tag => tag.name.toLowerCase())
      const allTagNames = allTags.map(tag => tag.name.toLowerCase())
      const tagsNotPresent = DEFAULT_TAGS.filter(
        tag => !allTagNames.includes(tag.name.toLowerCase()),
      )

      const tags: { id?: string; name: string; default?: boolean }[] = [
        ...allTags,
        ...tagsNotPresent,
      ]
      tags.sort((a, b) => a.name.localeCompare(b.name))

      tags.forEach(tag => {
        if (defaultTagNames.includes(tag.name.toLowerCase())) {
          tag.default = true
        }
      })

      return tags
    }, [allTags])

  const taggedProfileNames = profileTags.map(tag => tag.name.toLowerCase())

  if (taggingState === 'none') return null
  if (!identity) return null

  return (
    <Modal
      className={styles.taggingModal}
      close={() => {
        setTaggingState('none')
      }}
    >
      <SectionCard
        cardId="tagging"
        title="Tagging"
        icon={Icons.Tag}
        className={`CE disc-type ${personality?.discType || ''} card`}
      >
        <div className="tags">
          {tagsWithDefaults.map(tag => (
            <div key={tag.id || tag.name} className="tag">
              <Checkbox
                value={tag.name}
                checked={taggedProfileNames.includes(tag.name.toLowerCase())}
                onChange={evt => {
                  if (evt.target.checked) {
                    addTag({ name: evt.target.value })
                  } else if (tag.id) {
                    removeTag(tag as { id: string })
                  }
                }}
              />
              {tag.name}

              {tag.id && !tag.default && (
                <Link
                  href="#"
                  className="remove-tag"
                  onClick={evt => {
                    evt.preventDefault()
                    deleteTag(tag as { id: string; name: string })
                  }}
                >
                  X
                </Link>
              )}
            </div>
          ))}
        </div>

        <div className="create-tag">
          <form
            onSubmit={evt => {
              evt.preventDefault()
              createTag({ name: newTagName }).then(() => {
                setNewTagName('')
              })
            }}
          >
            <input
              type="text"
              name="new-tag-name"
              value={newTagName}
              placeholder="Add your own"
              onChange={evt => {
                setNewTagName(evt.target.value)
              }}
              required
            />

            <button type="submit" className="CE action-button">
              Create New Tag
            </button>
          </form>
        </div>

        <div className="buttons">
          <button
            className="CE action-button"
            onClick={() => setTaggingState('none')}
          >
            Finish Tagging
          </button>
        </div>
      </SectionCard>
    </Modal>
  )
}
